import React, { useState } from "react"
import useTitle from "@/hooks/useTitle"
import { useNavigate } from "react-router"
import PulseLoader from "react-spinners/PulseLoader"

import {
  PlusIcon,
  MinusIcon,
  TrashIcon,
  PencilSquareIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline"

import {
  FlagIcon,
  EnvelopeIcon,
  Squares2X2Icon,
  ArrowDownOnSquareIcon,
  ArrowUpOnSquareIcon,
  UserGroupIcon,
  BuildingOffice2Icon,
  ArrowUpRightIcon,
  ArrowDownRightIcon,
  ArrowsPointingInIcon,
  ArrowsPointingOutIcon,
} from "@heroicons/react/24/solid"

import {
  addIcon,
  userProfileIcon2,
} from "@/assets/img/imgAssets"

import {
  useGetUsersQuery,
  useGetUserPicQuery,
} from "./usersApiSlice"

import {
  useGetUserOrgSubscriptionsQuery,
  useCreateNewUserOrgSubscriptionMutation,
  useUpdateUserOrgSubscriptionMutation,
} from "@/features/userSubscriptions/userOrgSubscriptionsApiSlice"

import {
  useGetOrgSubscriptionsQuery,
} from "@/features/orgSubscriptions/orgSubscriptionsApiSlice"

import {
  useGetUserTrailSessionPlansQuery,
  useCreateNewUserTrailSessionPlanMutation,
} from "@/features/userTrailSessions/userTrailSessionPlansApiSlice"

import {
  useGetSessionsQuery,
} from "@/features/sessions/sessionsApiSlice"

import {
  useGetOrgTrailSessionCohortsQuery,
} from "@/pages/admin/orgTrailSessionCohorts/orgTrailSessionCohortsApiSlice"


const EtUserList = ({
  setActiveAdminView,
  onAdd,
  onUpdate,
  onDelete,
  setSelectedEntityId,
}) => {

  // First Created - 10 May 2024
  // Author - Atul Pandey
  // Updated - 14 Jun 2026 - changes for cohort based assignment
  // Updated - 03 Oct 2026 - added organization selector and user search
  // Updated - 03 Oct 2026 - added Active / Inactive / All user filtering


  /*
  |--------------------------------------------------------------------------
  | 0 - Page Title
  |--------------------------------------------------------------------------
  */

  useTitle("Users List")


  /*
  |--------------------------------------------------------------------------
  | 1 - Data
  |--------------------------------------------------------------------------
  */

  const {
    data: usersData,
    isLoading,
    isError,
    error,
  } =
    useGetUsersQuery()


  const {
    data: orgSubscriptionsData,
  } =
    useGetOrgSubscriptionsQuery()


  const {
    data: userOrgSubscriptionsData,
  } =
    useGetUserOrgSubscriptionsQuery()


  const [
    createNewUserOrgSubscription,
  ] =
    useCreateNewUserOrgSubscriptionMutation()


  const [
    updateUserOrgSubscription,
  ] =
    useUpdateUserOrgSubscriptionMutation()


  const [
    createNewUserTrailSessionPlan,
  ] =
    useCreateNewUserTrailSessionPlanMutation()


  const {
    data: userTrailSessionPlansData,
  } =
    useGetUserTrailSessionPlansQuery()


  const {
    data: userSessionsData,
  } =
    useGetSessionsQuery()


  const {
    data: orgTrailSessionCohortsData,
  } =
    useGetOrgTrailSessionCohortsQuery({
      active: true,
    })


  /*
  |--------------------------------------------------------------------------
  | 2 - State
  |--------------------------------------------------------------------------
  */

  const [
    selectedOrgId,
    setSelectedOrgId,
  ] =
    useState("")


  const [
    userSearch,
    setUserSearch,
  ] =
    useState("")


  /*
  |--------------------------------------------------------------------------
  | NEW - Active / Inactive / All
  |--------------------------------------------------------------------------
  */

  const [
    userStatusFilter,
    setUserStatusFilter,
  ] =
    useState("active")


  const [
    selectedUser,
    setSelectedUser,
  ] =
    useState(null)


  const [
    selectedSubscriptions,
    setSelectedSubscriptions,
  ] =
    useState({})


  const [
    showSubscriptions,
    setShowSubscriptions,
  ] =
    useState({})


  const [
    showOrganizations,
    setShowOrganizations,
  ] =
    useState(true)


  const [
    showTrails,
    setShowTrails,
  ] =
    useState({})


  const [
    showUserDetails,
    setShowUserDetails,
  ] =
    useState({})


  const [
    selectedCohorts,
    setSelectedCohorts,
  ] =
    useState({})


  /*
  |--------------------------------------------------------------------------
  | 3 - Adapter Helper
  |--------------------------------------------------------------------------
  */

  const extractEntitiesFromAdapter = (
    dataAdapter
  ) => {

    if (
      !dataAdapter ||
      !dataAdapter.ids ||
      !dataAdapter.entities
    ) {
      return []
    }


    return dataAdapter.ids.map(
      id =>
        dataAdapter.entities[id]
    )
  }


  /*
  |--------------------------------------------------------------------------
  | 4 - Filter Subscriptions For User Org
  |--------------------------------------------------------------------------
  */

  const getFilteredSubscriptions = (
    userId,
    userOrgId
  ) => {

    if (
      !orgSubscriptionsData &&
      !userOrgSubscriptionsData
    ) {
      return []
    }


    const orgSubsForUserOrg =
      orgSubscriptionsData
        ?.ids
        ?.filter(
          id =>
            orgSubscriptionsData
              ?.entities[id]
              ?.orgId
              ?._id ===
            userOrgId
              ?._id
        )


    let assignedSubs = []


    if (
      userOrgSubscriptionsData &&
      userOrgSubscriptionsData?.entities
    ) {

      const userSubs =
        Object
          .values(
            userOrgSubscriptionsData.entities
          )
          .filter(
            userSub =>
              userSub
                .userId
                ._id
                .toString() ===
              userId
                .toString()
          )


      assignedSubs =
        userSubs.flatMap(
          userSub =>
            userSub
              .userOrgSubscriptions
              .map(
                sub =>
                  sub
                    .orgSubscription
                    ._id
                    ?.toString()
              )
        )
    }


    return orgSubsForUserOrg
      ?.filter(
        id =>
          !assignedSubs.includes(
            id.toString()
          )
      )
  }


  /*
  |--------------------------------------------------------------------------
  | 5 - Filtered Subscription Map
  |--------------------------------------------------------------------------
  */

  const filteredSubscriptions =
    usersData
      ?.ids
      ?.reduce(
        (
          acc,
          userId
        ) => {

          const user =
            usersData
              ?.entities[userId]


          acc[userId] =
            getFilteredSubscriptions(
              userId,
              user.userOrgId
            )


          return acc
        },
        {}
      ) ||
    {}


  /*
  |--------------------------------------------------------------------------
  | 6 - Assigned Subscriptions
  |--------------------------------------------------------------------------
  */

  const getAssignedSubscriptions = (
    userId
  ) => {

    if (
      !userOrgSubscriptionsData
    ) {
      return []
    }


    const userSubs =
      Object
        .values(
          userOrgSubscriptionsData
            ?.entities ||
          {}
        )
        .filter(
          userSub =>
            userSub
              .userId
              ._id
              .toString() ===
            userId.toString()
        )


    return userSubs.flatMap(
      userSub =>
        userSub
          .userOrgSubscriptions
    )
  }


  /*
  |--------------------------------------------------------------------------
  | 7 - User Trail Session Plans
  |--------------------------------------------------------------------------
  */

  const getUserTrailSessionPlans = (
    userId
  ) => {

    if (
      !userTrailSessionPlansData ||
      !userTrailSessionPlansData
        ?.entities
    ) {
      return []
    }


    return Object
      .values(
        userTrailSessionPlansData
          .entities
      )
      .filter(
        plan =>
          plan
            .userId
            ._id
            .toString() ===
          userId.toString()
      )
  }


  const sessionsData =
    extractEntitiesFromAdapter(
      userSessionsData
    )


  /*
  |--------------------------------------------------------------------------
  | 8 - UI Handlers
  |--------------------------------------------------------------------------
  */

  const handleSelectSubscription = (
    userId,
    subscriptionId
  ) => {

    setSelectedSubscriptions(
      prev => ({
        ...prev,

        [userId]:
          subscriptionId,
      })
    )
  }


  const handleToggleSubscriptions = (
    userId
  ) => {

    setShowSubscriptions(
      prev => ({
        ...prev,

        [userId]:
          !prev[userId],
      })
    )
  }


  const handleToggleTrails = (
    subscriptionId
  ) => {

    setShowTrails(
      prev => ({
        ...prev,

        [subscriptionId]:
          !prev[
            subscriptionId
          ],
      })
    )
  }


  const handleToggleUserDetails = (
    userId
  ) => {

    setShowUserDetails(
      prev => ({
        ...prev,

        [userId]:
          !prev[userId],
      })
    )
  }


  /*
  |--------------------------------------------------------------------------
  | 9 - Resolve Cohort
  |--------------------------------------------------------------------------
  */

  const getUserCohortForTrail = (
    userId,
    trailId
  ) => {

    if (
      !orgTrailSessionCohortsData
        ?.entities
    ) {
      return null
    }


    const cohorts =
      Object.values(
        orgTrailSessionCohortsData
          .entities
      )


    return cohorts.find(
      cohort => {

        const cohortTrailId =
          cohort
            ?.trailId
            ?._id ||
          cohort
            ?.trailId
            ?.id ||
          cohort
            ?.trailId


        const isSameTrail =
          cohortTrailId
            ?.toString() ===
          trailId
            ?.toString()


        const isUserInCohort =
          cohort
            ?.cohortUserIds
            ?.some(
              cohortUserItem => {

                const cohortUserId =
                  cohortUserItem
                    ?.userId
                    ?._id ||
                  cohortUserItem
                    ?.userId
                    ?.id ||
                  cohortUserItem
                    ?.userId


                return (
                  cohortUserId
                    ?.toString() ===
                    userId
                      ?.toString() &&
                  cohortUserItem
                    ?.active !==
                    false
                )
              }
            )


        return (
          isSameTrail &&
          isUserInCohort &&
          cohort?.active !== false
        )
      }
    )
  }


  /*
  |--------------------------------------------------------------------------
  | 10 - Organizations
  |--------------------------------------------------------------------------
  */

  const organizations = {}


  usersData
    ?.ids
    ?.forEach(
      id => {

        const user =
          usersData
            .entities[id]


        const orgId =
          user.userOrgId
            ? user
                .userOrgId
                ?._id
            : "noOrg"


        if (
          !organizations[orgId]
        ) {

          organizations[orgId] = {

            orgName:
              user.userOrgId
                ? user
                    .userOrgId
                    .organizationName
                : "No Org",

            orgEstablishDate:
              user.userOrgId
                ? user
                    .userOrgId
                    .organizationStartedDate
                : null,

            users: [],
          }
        }


        organizations[
          orgId
        ].users.push(
          user
        )
      }
    )


  /*
  |--------------------------------------------------------------------------
  | 11 - Organization Selector
  |--------------------------------------------------------------------------
  */

  const organizationEntries =
    Object
      .entries(
        organizations
      )
      .sort(
        (
          [
            ,
            a,
          ],
          [
            ,
            b,
          ]
        ) =>
          (
            a?.orgName ||
            ""
          )
            .localeCompare(
              b?.orgName ||
              ""
            )
      )


  const effectiveSelectedOrgId =
    selectedOrgId ||
    organizationEntries
      ?.[0]
      ?.[0] ||
    ""


  const selectedOrganization =
    organizations
      ?.[
        effectiveSelectedOrgId
      ] ||
    null


  /*
  |--------------------------------------------------------------------------
  | 12 - User Status Counts
  |--------------------------------------------------------------------------
  */

  const selectedOrgUsers =
    selectedOrganization
      ?.users ||
    []


  const activeUsersCount =
    selectedOrgUsers.filter(
      user =>
        user?.active !==
        false
    ).length


  const inactiveUsersCount =
    selectedOrgUsers.filter(
      user =>
        user?.active ===
        false
    ).length


  const allUsersCount =
    selectedOrgUsers.length


  /*
  |--------------------------------------------------------------------------
  | 13 - Status Filter
  |--------------------------------------------------------------------------
  */

  const statusFilteredUsers =
    selectedOrgUsers.filter(
      user => {

        if (
          userStatusFilter ===
          "all"
        ) {
          return true
        }


        if (
          userStatusFilter ===
          "inactive"
        ) {
          return (
            user?.active ===
            false
          )
        }


        return (
          user?.active !==
          false
        )
      }
    )


  /*
  |--------------------------------------------------------------------------
  | 14 - Search Filter
  |--------------------------------------------------------------------------
  */

  const normalizedUserSearch =
    userSearch
      .trim()
      .toLowerCase()


  const filteredOrgUsers =
    statusFilteredUsers
      .filter(
        user => {

          if (
            !normalizedUserSearch
          ) {
            return true
          }


          const userName =
            (
              user?.name ||
              ""
            )
              .toLowerCase()


          const userEmail =
            (
              user?.email ||
              ""
            )
              .toLowerCase()


          return (
            userName.includes(
              normalizedUserSearch
            ) ||
            userEmail.includes(
              normalizedUserSearch
            )
          )
        }
      )


  /*
  |--------------------------------------------------------------------------
  | 15 - Assign Subscription
  |--------------------------------------------------------------------------
  */

  const handleAssignSubscription =
    async (
      userId
    ) => {

      try {

        await createNewUserOrgSubscription({
          userId,

          userOrgSubscriptions: [
            {
              orgSubscription:
                selectedSubscriptions[
                  userId
                ],
            },
          ],
        }).unwrap()


        alert(
          "Subscription assigned successfully"
        )


        setSelectedUser(
          null
        )


        setSelectedSubscriptions(
          prev => ({
            ...prev,

            [userId]:
              "",
          })
        )

      } catch (
        err
      ) {

        console.error(
          "Failed to assign subscription",
          err
        )


        alert(
          "Failed to assign subscription"
        )
      }
    }


  /*
  |--------------------------------------------------------------------------
  | 16 - Assign All Subscriptions
  |--------------------------------------------------------------------------
  */

  const handleAssignAllSubscriptions =
    async (
      userId
    ) => {

      try {

        const remainingSubscriptions =
          orgSubscriptionsData
            ?.ids
            ?.filter(
              id =>
                !(
                  usersData
                    .entities[
                      userId
                    ]
                    .userOrgSubscriptions ??
                  []
                )
                  .some(
                    sub =>
                      sub
                        .orgSubscription ===
                      id
                  ) &&
                orgSubscriptionsData
                  .entities[id]
                  .orgId
                  ?._id ===
                usersData
                  .entities[
                    userId
                  ]
                  .userOrgId
                  ?._id
            )
            .map(
              id => ({
                orgSubscription:
                  id,
              })
            )


        await createNewUserOrgSubscription({
          userId,

          userOrgSubscriptions:
            remainingSubscriptions,
        }).unwrap()


        alert(
          "All subscriptions assigned successfully"
        )


        setSelectedUser(
          null
        )

      } catch (
        err
      ) {

        console.error(
          "Failed to assign all subscriptions",
          err
        )


        alert(
          "Failed to assign all subscriptions"
        )
      }
    }


  /*
  |--------------------------------------------------------------------------
  | 17 - Get User Subscriptions
  |--------------------------------------------------------------------------
  */

  const getUserSubscriptions = (
    userId
  ) => {

    if (
      !userOrgSubscriptionsData
    ) {
      return []
    }


    const userSubs =
      Object
        .values(
          userOrgSubscriptionsData
            ?.entities ||
          {}
        )
        .filter(
          userSub =>
            userSub
              .userId
              ._id
              .toString() ===
            userId.toString()
        )


    return userSubs.flatMap(
      userSub =>
        userSub
          .userOrgSubscriptions
    )
  }


  /*
  |--------------------------------------------------------------------------
  | 18 - Remove Subscription
  |--------------------------------------------------------------------------
  */

  const handleRemoveSubscription =
    async (
      userId,
      subscriptionId
    ) => {

      try {

        const userSub =
          Object
            .values(
              userOrgSubscriptionsData
                ?.entities ||
              {}
            )
            .find(
              userSub =>
                userSub
                  .userId
                  ._id
                  .toString() ===
                userId
                  .toString()
            )


        if (
          !userSub
        ) {
          return
        }


        const updatedSubscriptions =
          userSub
            .userOrgSubscriptions
            .filter(
              sub =>
                sub
                  .orgSubscription
                  ._id
                  .toString() !==
                subscriptionId
            )


        await updateUserOrgSubscription({
          id:
            userSub._id,

          userOrgSubscriptions:
            updatedSubscriptions,
        }).unwrap()


        alert(
          "Subscription removed successfully"
        )


        setSelectedSubscriptions(
          prev => ({
            ...prev,

            [userId]:
              "",
          })
        )

      } catch (
        err
      ) {

        console.error(
          "Failed to remove subscription",
          err
        )


        alert(
          "Failed to remove subscription"
        )
      }
    }


  /*
  |--------------------------------------------------------------------------
  | 19 - Remove All Subscriptions
  |--------------------------------------------------------------------------
  */

  const handleRemoveAllSubscriptions =
    async (
      userId
    ) => {

      try {

        const userSub =
          Object
            .values(
              userOrgSubscriptionsData
                ?.entities ||
              {}
            )
            .find(
              userSub =>
                userSub
                  .userId
                  ._id
                  .toString() ===
                userId
                  .toString()
            )


        if (
          !userSub
        ) {
          return
        }


        await updateUserOrgSubscription({
          id:
            userSub._id,

          userOrgSubscriptions:
            [],
        }).unwrap()


        alert(
          "All subscriptions removed successfully"
        )


        setSelectedSubscriptions(
          prev => ({
            ...prev,

            [userId]:
              "",
          })
        )

      } catch (
        err
      ) {

        console.error(
          "Failed to remove all subscriptions",
          err
        )


        alert(
          "Failed to remove all subscriptions"
        )
      }
    }


  /*
  |--------------------------------------------------------------------------
  | 20 - Subscription Status
  |--------------------------------------------------------------------------
  */

  const allSubscriptionsAssigned = (
    userId
  ) => {

    const filteredSubs =
      filteredSubscriptions[
        userId
      ] ||
      []


    return (
      filteredSubs.length ===
      0
    )
  }


  const allSubscriptionsRemoved = (
    userId
  ) => {

    const userSubs =
      getUserSubscriptions(
        userId
      )


    return (
      userSubs.length ===
      0
    )
  }


  /*
  |--------------------------------------------------------------------------
  | 21 - Assign Sessions
  |--------------------------------------------------------------------------
  */

  const handleAssignSessions =
    async (
      userId,
      trailId
    ) => {

      const matchingCohort =
        getUserCohortForTrail(
          userId,
          trailId
        )


      if (
        !matchingCohort?._id
      ) {

        alert(
          "User is not added to a cohort for this trail."
        )

        return
      }


      try {

        await createNewUserTrailSessionPlan({
          userId,
          trailId,

          cohortId:
            matchingCohort._id,
        }).unwrap()


        alert(
          "Sessions assigned successfully"
        )

      } catch (
        err
      ) {

        console.error(
          "Failed to assign sessions",
          err
        )


        alert(
          err
            ?.data
            ?.message ||
          "Failed to assign sessions"
        )
      }
    }


  /*
  |--------------------------------------------------------------------------
  | 22 - Check Session Assignment
  |--------------------------------------------------------------------------
  */

  const allSessionsAssigned = (
    userId,
    trailId
  ) => {

    const matchingCohort =
      getUserCohortForTrail(
        userId,
        trailId
      )


    if (
      !matchingCohort?._id
    ) {
      return false
    }


    const userTrailPlans =
      getUserTrailSessionPlans(
        userId
      )


    const allSessionIdsForTrail =
      sessionsData
        ?.filter(
          session =>
            session
              .trailId
              ?._id
              ?.toString() ===
            trailId.toString()
        )
        .map(
          session =>
            session
              ._id
              .toString()
        ) ||
      []


    const assignedSessionIds =
      userTrailPlans
        ?.filter(
          plan => {

            const planTrailId =
              plan
                .trailId
                ?._id ||
              plan
                .trailId


            const planCohortId =
              plan
                .cohortId
                ?._id ||
              plan
                .cohortId


            return (
              planTrailId
                ?.toString() ===
                trailId
                  .toString() &&
              planCohortId
                ?.toString() ===
                matchingCohort
                  ._id
                  .toString()
            )
          }
        )
        .map(
          plan =>
            plan
              .sessionId
              ?._id
              ?.toString() ||
            plan
              .sessionId
              ?.toString()
        )
        .filter(
          Boolean
        ) ||
      []


    return (
      allSessionIdsForTrail
        .length >
        0 &&
      allSessionIdsForTrail
        .every(
          id =>
            assignedSessionIds
              .includes(
                id
              )
        )
    )
  }


  /*
  |--------------------------------------------------------------------------
  | Loading / Error
  |--------------------------------------------------------------------------
  */

  if (
    isLoading
  ) {

    return (
      <PulseLoader
        color="#ea580c"
      />
    )
  }


  if (
    isError
  ) {

    return (

      <div>
        Error occurred:{" "}
        {
          error
            ?.data
            ?.message
        }
      </div>
    )
  }


  /*
  |--------------------------------------------------------------------------
  | JSX
  |--------------------------------------------------------------------------
  */

  return (

    <div className="bg-gray-200 p-1 mx-auto mt-5 rounded-lg shadow-md shadow-black font-poppins">

      {/* Header */}
      <div className="m-1 p-1 flex justify-between items-center">

        <div className="flex justify-start items-center">

          <div
            className="flex justify-center items-center cursor-pointer rounded-full h-7 w-7 hover:bg-white"
            onClick={
              () =>
                setShowOrganizations(
                  prev =>
                    !prev
                )
            }
          >

            {
              showOrganizations
                ? (
                  <MinusIcon className="h-5 w-5 text-orange-600" />
                )
                : (
                  <PlusIcon className="h-5 w-5 text-orange-600" />
                )
            }

          </div>


          <div>

            <h1 className="p-1 m-1 font-bold font-poppins text-lg">
              Users
            </h1>

          </div>


          {/* Global Organization Count */}
          <div className="flex justify-start items-center m-1 p-1">

            <p className="text-[10px] text-gray-400 mx-2 px-2">
              Organizations:
            </p>


            <BuildingOffice2Icon className="h-5 w-5 text-orange-600 m-1" />


            <div className="rounded-full flex justify-center items-center h-5 min-w-5 px-1 border border-orange-300 bg-gray-50">

              <p className="text-orange-600 text-[10px]">

                {
                  Object
                    .entries(
                      organizations
                    )
                    .length
                }

              </p>

            </div>

          </div>


          {/* Global User Count */}
          <div className="flex justify-start items-center m-1 p-1">

            <p className="text-[10px] text-gray-400 mx-2 px-2">
              Users:
            </p>


            <UserGroupIcon className="h-5 w-5 text-orange-600 m-1" />


            <div className="rounded-full flex justify-center items-center h-5 min-w-5 px-1 border border-orange-300 bg-gray-50">

              <p className="text-orange-600 text-[10px]">

                {
                  usersData
                    ?.ids
                    ?.length ||
                  0
                }

              </p>

            </div>

          </div>

        </div>


        {/* Add User */}
        <div>

          <img
            src={
              addIcon
            }
            alt=""
            className="h-10 w-10 cursor-pointer"
            onClick={
              onAdd
            }
          />

        </div>

      </div>


      {
        showOrganizations &&
        (

          <div className="bg-white m-1 p-2 rounded">


            {/* Organization / Search Bar */}
            <div className="bg-gray-100 rounded m-1 p-2">

              <div className="flex flex-wrap justify-between items-end gap-3">


                {/* Organization Selector */}
                <div className="flex-1 min-w-[260px]">

                  <p className="text-[10px] font-bold text-gray-500 mb-1">
                    Organization
                  </p>


                  <div className="flex justify-start items-center">

                    <BuildingOffice2Icon className="h-5 w-5 text-orange-600 mr-2" />


                    <select
                      className="w-full p-2 border border-gray-300 rounded text-xs bg-white"
                      value={
                        effectiveSelectedOrgId
                      }
                      onChange={
                        e => {

                          setSelectedOrgId(
                            e
                              .target
                              .value
                          )


                          setUserSearch(
                            ""
                          )


                          /*
                          |------------------------------------------------------
                          | Default back to Active whenever org changes
                          |------------------------------------------------------
                          */

                          setUserStatusFilter(
                            "active"
                          )
                        }
                      }
                    >

                      {
                        organizationEntries
                          .map(
                            (
                              [
                                orgId,
                                {
                                  orgName,
                                },
                              ]
                            ) => (

                              <option
                                key={
                                  orgId
                                }
                                value={
                                  orgId
                                }
                              >

                                {
                                  orgName
                                }

                              </option>

                            )
                          )
                      }

                    </select>

                  </div>

                </div>


                {/* User Search */}
                <div className="flex-1 min-w-[300px]">

                  <p className="text-[10px] font-bold text-gray-500 mb-1">
                    Find User
                  </p>


                  <div className="flex justify-start items-center bg-white border border-gray-300 rounded px-2">

                    <MagnifyingGlassIcon className="h-4 w-4 text-orange-600 mr-1" />


                    <input
                      type="text"
                      value={
                        userSearch
                      }
                      onChange={
                        e =>
                          setUserSearch(
                            e
                              .target
                              .value
                          )
                      }
                      placeholder="Search by name or email..."
                      className="w-full p-2 text-xs outline-none bg-transparent"
                    />


                    {
                      userSearch &&
                      (

                        <button
                          type="button"
                          onClick={
                            () =>
                              setUserSearch(
                                ""
                              )
                          }
                          className="text-[10px] text-gray-400 hover:text-orange-600 px-2"
                        >
                          Clear
                        </button>

                      )
                    }

                  </div>

                </div>

              </div>


              {/* Selected Organization Summary */}
              {
                selectedOrganization &&
                (

                  <div className="mt-3 pt-2 border-t border-gray-200 flex flex-wrap justify-between items-center gap-3">

                    <div className="flex justify-start items-center">

                      <h2 className="font-bold text-sm mr-4">

                        {
                          selectedOrganization
                            .orgName
                        }

                      </h2>


                      <div className="flex justify-start items-center">

                        <p className="text-gray-500 text-[10px] mr-1">
                          Established:
                        </p>


                        <FlagIcon className="h-4 w-4 text-orange-600" />


                        <p className="font-bold text-[10px] mx-1">

                          {
                            formatDate(
                              selectedOrganization
                                .orgEstablishDate
                            )
                          }

                        </p>

                      </div>

                    </div>


                    {/* NEW - User Status Chips */}
                    <div className="flex justify-start items-center gap-1">


                      {/* Active */}
                      <button
                        type="button"
                        onClick={
                          () =>
                            setUserStatusFilter(
                              "active"
                            )
                        }
                        className={
                          `flex justify-center items-center rounded-full border px-3 py-1 transition ${
                            userStatusFilter ===
                            "active"
                              ? "bg-orange-600 border-orange-600 text-white"
                              : "bg-white border-orange-300 text-gray-600 hover:bg-orange-50"
                          }`
                        }
                      >

                        <span className="text-[10px] font-bold">
                          Active
                        </span>


                        <span
                          className={
                            `ml-2 rounded-full min-w-5 h-5 px-1 flex justify-center items-center text-[9px] ${
                              userStatusFilter ===
                              "active"
                                ? "bg-white text-orange-600"
                                : "bg-orange-100 text-orange-700"
                            }`
                          }
                        >

                          {
                            activeUsersCount
                          }

                        </span>

                      </button>


                      {/* Inactive */}
                      <button
                        type="button"
                        onClick={
                          () =>
                            setUserStatusFilter(
                              "inactive"
                            )
                        }
                        className={
                          `flex justify-center items-center rounded-full border px-3 py-1 transition ${
                            userStatusFilter ===
                            "inactive"
                              ? "bg-gray-600 border-gray-600 text-white"
                              : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                          }`
                        }
                      >

                        <span className="text-[10px] font-bold">
                          Inactive
                        </span>


                        <span
                          className={
                            `ml-2 rounded-full min-w-5 h-5 px-1 flex justify-center items-center text-[9px] ${
                              userStatusFilter ===
                              "inactive"
                                ? "bg-white text-gray-600"
                                : "bg-gray-200 text-gray-600"
                            }`
                          }
                        >

                          {
                            inactiveUsersCount
                          }

                        </span>

                      </button>


                      {/* All */}
                      <button
                        type="button"
                        onClick={
                          () =>
                            setUserStatusFilter(
                              "all"
                            )
                        }
                        className={
                          `flex justify-center items-center rounded-full border px-3 py-1 transition ${
                            userStatusFilter ===
                            "all"
                              ? "bg-orange-100 border-orange-400 text-orange-700"
                              : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                          }`
                        }
                      >

                        <span className="text-[10px] font-bold">
                          All
                        </span>


                        <span
                          className={
                            `ml-2 rounded-full min-w-5 h-5 px-1 flex justify-center items-center text-[9px] ${
                              userStatusFilter ===
                              "all"
                                ? "bg-orange-600 text-white"
                                : "bg-gray-200 text-gray-600"
                            }`
                          }
                        >

                          {
                            allUsersCount
                          }

                        </span>

                      </button>

                    </div>

                  </div>

                )
              }

            </div>


            {/* Selected Organization Users */}
            <div className="bg-white m-1 p-1 rounded">

              {
                !selectedOrganization
                  ? (

                    <div className="m-2 p-4 rounded bg-gray-100 text-center">

                      <p className="text-xs text-gray-500">
                        No organizations available.
                      </p>

                    </div>

                  )
                  : filteredOrgUsers
                      .length ===
                    0
                    ? (

                      <div className="m-2 p-4 rounded bg-gray-100 text-center">

                        <p className="text-xs text-gray-500">

                          No{" "}

                          {
                            userStatusFilter ===
                            "all"
                              ? ""
                              : `${userStatusFilter} `
                          }

                          users found

                          {
                            userSearch
                              ? ` for "${userSearch}"`
                              : ""
                          }.

                        </p>

                      </div>

                    )
                    : (

                      <>

                        {
                          filteredOrgUsers
                            .map(
                              (
                                user,
                                userIndex
                              ) => (

                                <div
                                  key={
                                    user.id +
                                    userIndex
                                  }
                                  className="bg-gray-100 rounded m-1 p-1"
                                >

                                  {/* User Header */}
                                  <div className="flex justify-between items-center">

                                    <div className="m-1 p-1 flex justify-start items-center">

                                      <div
                                        className="flex justify-center items-center cursor-pointer rounded-full h-7 w-7 hover:bg-white"
                                        onClick={
                                          () =>
                                            handleToggleUserDetails(
                                              user.id
                                            )
                                        }
                                      >

                                        {
                                          showUserDetails[
                                            user.id
                                          ]
                                            ? (

                                              <MinusIcon className="h-5 w-5 text-orange-600" />

                                            )
                                            : (

                                              <PlusIcon className="h-5 w-5 text-orange-600" />

                                            )
                                        }

                                      </div>


                                      {/* User Pic + Name */}
                                      <div className="flex justify-start items-center m-1 p-1 w-64">

                                        <User
                                          username={
                                            user.username
                                          }
                                          defaultUserPic={
                                            userProfileIcon2
                                          }
                                        />


                                        <div>

                                          <p className="text-xs m-1 p-1">
                                            {
                                              user.name
                                            }
                                          </p>


                                          {/* Optional small status */}
                                          <div className="mx-2">

                                            {
                                              user?.active ===
                                              false
                                                ? (

                                                  <span className="text-[8px] px-2 py-[2px] rounded-full bg-gray-200 text-gray-500">
                                                    Inactive
                                                  </span>

                                                )
                                                : (

                                                  <span className="text-[8px] px-2 py-[2px] rounded-full bg-orange-100 text-orange-700">
                                                    Active
                                                  </span>

                                                )
                                            }

                                          </div>

                                        </div>

                                      </div>


                                      {/* Email */}
                                      <div className="flex justify-start items-center m-1 p-1">

                                        <EnvelopeIcon className="h-5 w-5 text-orange-600" />


                                        <p className="text-xs m-1 p-1">
                                          {
                                            user.email
                                          }
                                        </p>

                                      </div>

                                    </div>


                                    {/* Edit / Delete */}
                                    <div className="flex justify-start items-center mx-2">

                                      <div className="rounded-full flex justify-center items-center h-6 w-6 hover:bg-white">

                                        <PencilSquareIcon
                                          onClick={
                                            () =>
                                              onUpdate(
                                                user?.id
                                              )
                                          }
                                          className="h-4 w-4 text-orange-600 cursor-pointer"
                                        />

                                      </div>


                                      <div className="rounded-full flex justify-center items-center h-6 w-6 hover:bg-white">

                                        <TrashIcon
                                          onClick={
                                            () =>
                                              onDelete(
                                                user?.id
                                              )
                                          }
                                          className="h-4 w-4 text-orange-600 cursor-pointer"
                                        />

                                      </div>

                                    </div>

                                  </div>


                                  {/* User Subscriptions */}
                                  {
                                    showUserDetails[
                                      user.id
                                    ] &&
                                    (

                                      <div className="m-1 p-1 rounded bg-gray-200">

                                        <div className="flex justify-start items-center m-1 p-1">

                                          <div
                                            className="flex justify-center items-center cursor-pointer rounded-full h-7 w-7 hover:bg-white"
                                            onClick={
                                              () =>
                                                handleToggleSubscriptions(
                                                  user.id
                                                )
                                            }
                                          >

                                            {
                                              showSubscriptions[
                                                user.id
                                              ]
                                                ? (

                                                  <MinusIcon className="h-5 w-5 text-orange-600" />

                                                )
                                                : (

                                                  <PlusIcon className="h-5 w-5 text-orange-600" />

                                                )
                                            }

                                          </div>


                                          <p className="text-xs font-bold mx-1 px-1">
                                            Subscriptions
                                          </p>

                                        </div>


                                        {
                                          showSubscriptions[
                                            user.id
                                          ] &&
                                          (

                                            <div className="m-1 p-1 rounded bg-white">

                                              <div className="m-1 p-1 rounded bg-gray-100 flex justify-between">


                                                {/* Current Subscriptions */}
                                                <div className="w-1/2">

                                                  <div className="flex justify-start items-center m-1 p-1">

                                                    <div className="m-1 p-1 rounded-full h-7 w-7 bg-gray-50 flex justify-center items-center">

                                                      <Squares2X2Icon className="h-5 w-5 text-orange-600" />

                                                    </div>


                                                    <p className="text-xs font-bold">
                                                      Current Subscriptions
                                                    </p>

                                                  </div>


                                                  {
                                                    getUserSubscriptions(
                                                      user.id
                                                    )
                                                      .length >
                                                      0
                                                      ? (

                                                        getUserSubscriptions(
                                                          user.id
                                                        )
                                                          .map(
                                                            sub => (

                                                              <div
                                                                key={
                                                                  sub
                                                                    .orgSubscription
                                                                    ._id
                                                                }
                                                                className="m-1 p-1 rounded bg-gray-200"
                                                              >

                                                                <div className="bg-gray-50 rounded m-1 p-1 w-full">

                                                                  <div className="m-1 p-1 flex justify-start items-center">

                                                                    <img
                                                                      src={
                                                                        sub
                                                                          .orgSubscription
                                                                          .subsPackId[0]
                                                                          .pic
                                                                      }
                                                                      alt=""
                                                                      className="h-5 w-5"
                                                                    />


                                                                    <p className="text-xs m-1 p-1">

                                                                      {
                                                                        sub
                                                                          .orgSubscription
                                                                          .subsPackId[0]
                                                                          .subscriptionPackName
                                                                      }

                                                                    </p>

                                                                  </div>


                                                                  {/* Trails */}
                                                                  <div className="flex justify-start items-center">

                                                                    <div
                                                                      className="cursor-pointer mx-1 px-1 flex justify-center items-center w-5 h-5 rounded-full hover:bg-gray-100"
                                                                      onClick={
                                                                        () =>
                                                                          handleToggleTrails(
                                                                            sub
                                                                              .orgSubscription
                                                                              ._id
                                                                          )
                                                                      }
                                                                    >

                                                                      {
                                                                        showTrails[
                                                                          sub
                                                                            .orgSubscription
                                                                            ._id
                                                                        ]
                                                                          ? (

                                                                            <MinusIcon className="h-3 w-3 text-orange-600" />

                                                                          )
                                                                          : (

                                                                            <PlusIcon className="h-3 w-3 text-orange-600" />

                                                                          )
                                                                      }

                                                                    </div>


                                                                    <p className="py-1 text-[10px] font-bold">
                                                                      Trails
                                                                    </p>

                                                                  </div>


                                                                  {
                                                                    showTrails[
                                                                      sub
                                                                        .orgSubscription
                                                                        ._id
                                                                    ] &&
                                                                    sub
                                                                      .orgSubscription
                                                                      ?.subsPackId[0]
                                                                      .subscriptionPackTrails
                                                                      ?.map(
                                                                        (
                                                                          trail,
                                                                          trailIndex
                                                                        ) => {

                                                                          const matchingCohort =
                                                                            getUserCohortForTrail(
                                                                              user._id,
                                                                              trail
                                                                                .packTrailId
                                                                                ._id
                                                                            )


                                                                          const isAssigned =
                                                                            allSessionsAssigned(
                                                                              user._id,
                                                                              trail
                                                                                .packTrailId
                                                                                ._id
                                                                            )


                                                                          const isDisabled =
                                                                            !matchingCohort ||
                                                                            isAssigned


                                                                          return (

                                                                            <div
                                                                              key={
                                                                                trail
                                                                                  .packTrailId
                                                                                  ._id +
                                                                                trailIndex
                                                                              }
                                                                            >

                                                                              <div className="flex justify-between items-center mx-1 px-1 bg-gray-100 rounded">

                                                                                <div className="flex justify-start items-center">

                                                                                  <p className="text-[10px] mx-1 px-1 font-bold">
                                                                                    {
                                                                                      trailIndex +
                                                                                      1
                                                                                    }.
                                                                                  </p>


                                                                                  <img
                                                                                    src={
                                                                                      trail
                                                                                        .packTrailId
                                                                                        .pic
                                                                                    }
                                                                                    alt=""
                                                                                    className="h-5 w-5"
                                                                                  />


                                                                                  <p className="text-[10px] mx-1 px-1">
                                                                                    {
                                                                                      trail
                                                                                        .packTrailId
                                                                                        .trailName
                                                                                    }
                                                                                  </p>

                                                                                </div>


                                                                                <p
                                                                                  className={
                                                                                    `text-[10px] rounded-full px-2 py-1 ${
                                                                                      matchingCohort
                                                                                        ? "bg-orange-100 text-orange-700"
                                                                                        : "bg-red-100 text-red-700"
                                                                                    }`
                                                                                  }
                                                                                >

                                                                                  {
                                                                                    matchingCohort
                                                                                      ? matchingCohort
                                                                                          .cohortCode ||
                                                                                        matchingCohort
                                                                                          .cohortName
                                                                                      : "No Cohort"
                                                                                  }

                                                                                </p>


                                                                                <div
                                                                                  className={
                                                                                    `rounded-full border ${
                                                                                      isDisabled
                                                                                        ? "border-gray-400 cursor-not-allowed text-gray-400"
                                                                                        : "border-orange-600 hover:bg-orange-600 cursor-pointer hover:text-white text-gray-600"
                                                                                    }`
                                                                                  }
                                                                                  onClick={
                                                                                    () => {

                                                                                      if (
                                                                                        !isDisabled
                                                                                      ) {

                                                                                        handleAssignSessions(
                                                                                          user._id,
                                                                                          trail
                                                                                            .packTrailId
                                                                                            ._id
                                                                                        )
                                                                                      }
                                                                                    }
                                                                                  }
                                                                                >

                                                                                  <p className="text-[10px] px-2">

                                                                                    {
                                                                                      isAssigned
                                                                                        ? "Sessions Assigned"
                                                                                        : "Assign Sessions"
                                                                                    }

                                                                                  </p>

                                                                                </div>

                                                                              </div>

                                                                            </div>

                                                                          )
                                                                        }
                                                                      )
                                                                  }

                                                                </div>

                                                              </div>

                                                            )
                                                          )

                                                      )
                                                      : (

                                                        <div className="m-1 p-1 rounded bg-gray-200 flex justify-center items-center">

                                                          <div className="m-1 p-1 bg-gray-50 rounded">

                                                            <p className="text-[10px] text-gray-800 p-2">

                                                              No current subscriptions assigned. Please assign subscriptions from the list of available subscriptions.

                                                            </p>

                                                          </div>

                                                        </div>

                                                      )
                                                  }

                                                </div>


                                                {/* Assign / Remove */}
                                                <div className="w-1/2">


                                                  {/* Assign */}
                                                  <div className="w-full">

                                                    <div className="flex justify-start items-center m-1 p-1">

                                                      <div className="m-1 p-1 rounded-full h-7 w-7 bg-gray-50 flex justify-center items-center">

                                                        <ArrowDownOnSquareIcon className="h-5 w-5 text-orange-600" />

                                                      </div>


                                                      <p className="text-xs font-bold">
                                                        Assign Subscriptions
                                                      </p>

                                                    </div>


                                                    <div className="bg-gray-200 rounded m-1 p-1">

                                                      <select
                                                        className="m-1 p-1 border border-gray-300 rounded text-[10px] w-full"
                                                        value={
                                                          selectedSubscriptions[
                                                            user.id
                                                          ] ||
                                                          ""
                                                        }
                                                        onChange={
                                                          e =>
                                                            handleSelectSubscription(
                                                              user.id,
                                                              e
                                                                .target
                                                                .value
                                                            )
                                                        }
                                                      >

                                                        <option value="">
                                                          Select a subscription
                                                        </option>


                                                        {
                                                          filteredSubscriptions[
                                                            user.id
                                                          ]
                                                            ?.map(
                                                              id => (

                                                                <option
                                                                  key={
                                                                    id
                                                                  }
                                                                  value={
                                                                    id
                                                                  }
                                                                >

                                                                  {
                                                                    orgSubscriptionsData
                                                                      ?.entities[id]
                                                                      .orgSubscriptionName
                                                                  }

                                                                </option>

                                                              )
                                                            )
                                                        }

                                                      </select>


                                                      <div className="flex justify-between items-center m-1 p-1">

                                                        <div
                                                          className={
                                                            `m-1 p-1 rounded-full bg-gray-50 flex justify-center items-center border ${
                                                              allSubscriptionsAssigned(
                                                                user.id
                                                              )
                                                                ? "cursor-not-allowed text-gray-400 border-gray-400"
                                                                : "cursor-pointer text-orange-600 hover:bg-orange-600 hover:text-white border-orange-600"
                                                            }`
                                                          }
                                                        >

                                                          <ArrowDownRightIcon className="h-5 w-5 p-1" />


                                                          <button
                                                            className="text-[10px] mx-2 px-3 py-1"
                                                            onClick={
                                                              () =>
                                                                handleAssignSubscription(
                                                                  user.id
                                                                )
                                                            }
                                                            disabled={
                                                              allSubscriptionsAssigned(
                                                                user.id
                                                              ) ||
                                                              !selectedSubscriptions[
                                                                user.id
                                                              ]
                                                            }
                                                          >
                                                            Assign
                                                          </button>

                                                        </div>


                                                        <div
                                                          className={
                                                            `m-1 p-1 rounded-full bg-gray-50 flex justify-center items-center border ${
                                                              allSubscriptionsAssigned(
                                                                user.id
                                                              )
                                                                ? "cursor-not-allowed text-gray-400 border-gray-400"
                                                                : "cursor-pointer text-orange-600 hover:bg-orange-600 hover:text-white border-orange-600"
                                                            }`
                                                          }
                                                        >

                                                          <ArrowsPointingInIcon className="h-5 w-5 p-[2px]" />


                                                          <button
                                                            className="text-[10px] mx-2 px-3 py-1"
                                                            onClick={
                                                              () =>
                                                                handleAssignAllSubscriptions(
                                                                  user.id
                                                                )
                                                            }
                                                            disabled={
                                                              allSubscriptionsAssigned(
                                                                user.id
                                                              )
                                                            }
                                                          >
                                                            Assign All
                                                          </button>

                                                        </div>

                                                      </div>

                                                    </div>

                                                  </div>


                                                  {/* Remove */}
                                                  <div className="w-full">

                                                    <div className="flex justify-start items-center m-1 p-1">

                                                      <div className="m-1 p-1 rounded-full h-7 w-7 bg-gray-50 flex justify-center items-center">

                                                        <ArrowUpOnSquareIcon className="h-5 w-5 text-orange-600" />

                                                      </div>


                                                      <p className="text-xs font-bold">
                                                        Remove Subscriptions
                                                      </p>

                                                    </div>


                                                    <div className="bg-gray-200 rounded m-1 p-1">

                                                      <select
                                                        className="m-1 p-1 border border-gray-300 rounded text-[10px] w-full"
                                                        value={
                                                          selectedSubscriptions[
                                                            user.id
                                                          ] ||
                                                          ""
                                                        }
                                                        onChange={
                                                          e =>
                                                            handleSelectSubscription(
                                                              user.id,
                                                              e
                                                                .target
                                                                .value
                                                            )
                                                        }
                                                      >

                                                        <option value="">
                                                          Select a subscription
                                                        </option>


                                                        {
                                                          getAssignedSubscriptions(
                                                            user.id
                                                          )
                                                            .map(
                                                              sub => (

                                                                <option
                                                                  key={
                                                                    sub
                                                                      .orgSubscription
                                                                      ._id
                                                                  }
                                                                  value={
                                                                    sub
                                                                      .orgSubscription
                                                                      ._id
                                                                  }
                                                                >

                                                                  {
                                                                    sub
                                                                      .orgSubscription
                                                                      .orgSubscriptionName
                                                                  }

                                                                </option>

                                                              )
                                                            )
                                                        }

                                                      </select>


                                                      <div className="flex justify-between items-center m-1 p-1">

                                                        <div
                                                          className={
                                                            `m-1 p-1 rounded-full bg-gray-50 flex justify-center items-center border ${
                                                              allSubscriptionsRemoved(
                                                                user.id
                                                              )
                                                                ? "cursor-not-allowed text-gray-400 border-gray-400"
                                                                : "cursor-pointer text-orange-600 hover:bg-orange-600 hover:text-white border-orange-600"
                                                            }`
                                                          }
                                                        >

                                                          <ArrowUpRightIcon className="h-5 w-5 p-1" />


                                                          <button
                                                            className="text-[10px] mx-2 px-3 py-1"
                                                            onClick={
                                                              () =>
                                                                handleRemoveSubscription(
                                                                  user.id,
                                                                  selectedSubscriptions[
                                                                    user.id
                                                                  ]
                                                                )
                                                            }
                                                            disabled={
                                                              allSubscriptionsRemoved(
                                                                user.id
                                                              ) ||
                                                              !selectedSubscriptions[
                                                                user.id
                                                              ]
                                                            }
                                                          >
                                                            Remove
                                                          </button>

                                                        </div>


                                                        <div
                                                          className={
                                                            `m-1 p-1 rounded-full bg-gray-50 flex justify-center items-center border ${
                                                              allSubscriptionsRemoved(
                                                                user.id
                                                              )
                                                                ? "cursor-not-allowed text-gray-400 border-gray-400"
                                                                : "cursor-pointer text-orange-600 hover:bg-orange-600 hover:text-white border-orange-600"
                                                            }`
                                                          }
                                                        >

                                                          <ArrowsPointingOutIcon className="h-5 w-5 p-[2px]" />


                                                          <button
                                                            className="text-[10px] mx-2 px-3 py-1"
                                                            onClick={
                                                              () =>
                                                                handleRemoveAllSubscriptions(
                                                                  user.id
                                                                )
                                                            }
                                                            disabled={
                                                              allSubscriptionsRemoved(
                                                                user.id
                                                              )
                                                            }
                                                          >
                                                            Remove All
                                                          </button>

                                                        </div>

                                                      </div>

                                                    </div>

                                                  </div>

                                                </div>

                                              </div>

                                            </div>

                                          )
                                        }

                                      </div>

                                    )
                                  }

                                </div>

                              )
                            )
                        }

                      </>

                    )
              }

            </div>

          </div>

        )
      }

    </div>
  )
}


/*
|--------------------------------------------------------------------------
| Format Date
|--------------------------------------------------------------------------
*/

const formatDate = (
  dateString
) => {

  if (
    !dateString
  ) {
    return "Unknown"
  }


  const date =
    new Date(
      dateString
    )


  if (
    isNaN(
      date
    )
  ) {
    return "Unknown"
  }


  return new Intl
    .DateTimeFormat(
      "en-GB",
      {
        year: "numeric",
      }
    )
    .format(
      date
    )
}


/*
|--------------------------------------------------------------------------
| User Pic
|--------------------------------------------------------------------------
*/

const User = ({
  username,
  defaultUserPic,
}) => {

  const {
    data: userPic,
    isFetching:
      isFetchingUserPic,
  } =
    useGetUserPicQuery(
      username
    )


  return (

    <div className="flex justify-center items-center h-12 w-12 rounded-full border border-white bg-gradient-to-r from-orange-600 to-orange-400 shadow-lg shadow-black cursor-pointer">

      {
        isFetchingUserPic
          ? (

            <div>
              Loading...
            </div>

          )
          : userPic
            ? (

              <img
                src={
                  userPic
                }
                alt="User"
                className="h-11 w-11 rounded-full"
              />

            )
            : (

              <img
                src={
                  defaultUserPic
                }
                alt="User"
                className="h-10 w-10 p-1"
              />

            )
      }

    </div>
  )
}


export default EtUserList